/**
 * Comprehensive Camera Cleanup Utility
 * This utility provides multiple fallback mechanisms to ensure camera hardware
 * is properly released when leaving a livestream page or application.
 */

let isCleanupInProgress = false;
let cleanupAttempts = 0;
const MAX_CLEANUP_ATTEMPTS = 3;

/**
 * Force stop all active media tracks with immediate effect
 * This function uses synchronous operations for critical path scenarios
 */
export const emergencyCameraCleanup = () => {
  if (isCleanupInProgress) {
    console.log('🔄 Emergency cleanup already in progress, skipping duplicate');
    return;
  }

  isCleanupInProgress = true;
  cleanupAttempts++;

  console.log(`🚨 EMERGENCY CAMERA CLEANUP (Attempt ${cleanupAttempts}/${MAX_CLEANUP_ATTEMPTS})`);

  try {
    let tracksReleased = 0;

    // 1. IMMEDIATE: Stop all video elements and their streams
    const videoElements = document.querySelectorAll('video');
    videoElements.forEach((video, index) => {
      console.log(`🎥 Processing video element ${index + 1}/${videoElements.length}`);

      if (video.srcObject) {
        const stream = video.srcObject;
        if (stream && typeof stream.getTracks === 'function') {
          stream.getTracks().forEach(track => {
            if (track.readyState === 'live') {
              track.stop();
              tracksReleased++;
              console.log(`🛑 EMERGENCY: Stopped ${track.kind} track: ${track.label || 'Unknown'}`);
            }
          });
        }
        // Immediately clear the video element
        video.srcObject = null;
        video.pause();
        video.load(); // Force reload to clear any cached streams
      }
    });

    // 2. IMMEDIATE: Clear global stream references
    if (window.currentMediaStream) {
      if (typeof window.currentMediaStream.getTracks === 'function') {
        window.currentMediaStream.getTracks().forEach(track => {
          if (track.readyState === 'live') {
            track.stop();
            tracksReleased++;
            console.log(`🛑 EMERGENCY: Stopped global ${track.kind} track: ${track.label || 'Unknown'}`);
          }
        });
      }
      window.currentMediaStream = null;
    }

    // 3. IMMEDIATE: Clear any media streams stored in React component refs
    // Check for common ref patterns used in React applications
    const commonRefNames = ['videoRef', 'streamRef', 'mediaStreamRef', 'localVideoRef'];
    commonRefNames.forEach(refName => {
      if (window[refName] && window[refName].current) {
        const element = window[refName].current;
        if (element && element.srcObject) {
          const stream = element.srcObject;
          if (stream && typeof stream.getTracks === 'function') {
            stream.getTracks().forEach(track => {
              if (track.readyState === 'live') {
                track.stop();
                tracksReleased++;
                console.log(`🛑 EMERGENCY: Stopped ref ${track.kind} track: ${track.label || 'Unknown'}`);
              }
            });
          }
          element.srcObject = null;
        }
      }
    });

    // 4. IMMEDIATE: Check for MediaRecorder instances and stop them
    if (window.mediaRecorder && window.mediaRecorder.state === 'recording') {
      window.mediaRecorder.stop();
      console.log('🛑 EMERGENCY: Stopped active MediaRecorder');
    }

    console.log(`✅ EMERGENCY CLEANUP: Released ${tracksReleased} media tracks`);

    // 5. DELAYED: Fallback cleanup with minimal new camera access (only if needed)
    if (tracksReleased === 0) {
      console.log('⚠️ No active tracks found, attempting fallback cleanup');
      setTimeout(() => {
        fallbackCameraCleanup();
      }, 50);
    }

  } catch (error) {
    console.error('❌ Emergency cleanup error:', error);
  } finally {
    // Reset cleanup flag after a short delay
    setTimeout(() => {
      isCleanupInProgress = false;
    }, 100);
  }
};

/**
 * Fallback cleanup method - only used when no active tracks are found
 * This creates minimal camera access to force release any lingering hardware locks
 */
const fallbackCameraCleanup = async () => {
  if (cleanupAttempts > MAX_CLEANUP_ATTEMPTS) {
    console.log('🛑 Max cleanup attempts reached, stopping fallback cleanup');
    return;
  }

  try {
    console.log('🔄 Attempting fallback camera cleanup');

    // Create minimal camera access with lowest possible quality
    const tempStream = await navigator.mediaDevices.getUserMedia({
      video: {
        width: { ideal: 160 },
        height: { ideal: 120 },
        frameRate: { ideal: 1 }
      },
      audio: false
    });

    // Immediately stop the temporary stream
    const videoTracks = tempStream.getVideoTracks();
    videoTracks.forEach(track => {
      track.stop();
      console.log(`🛑 FALLBACK: Force released camera: ${track.label || 'Unknown'}`);
    });

    console.log('✅ Fallback cleanup completed');
  } catch (error) {
    console.log('ℹ️ Fallback cleanup not needed or camera already released:', error.message);
  }
};

/**
 * Comprehensive cleanup function with async support
 * This is the main cleanup function to be called from components
 */
export const comprehensiveCameraCleanup = async () => {
  console.log('🧹 Starting comprehensive camera cleanup');

  // First, run emergency cleanup
  emergencyCameraCleanup();

  // Wait a moment for emergency cleanup to complete
  await new Promise(resolve => setTimeout(resolve, 150));

  // Then run additional checks and cleanup
  try {
    // Check for any remaining active tracks
    const devices = await navigator.mediaDevices.enumerateDevices();
    const videoDevices = devices.filter(device => device.kind === 'videoinput');

    console.log(`ℹ️ Found ${videoDevices.length} video input devices`);

    // Additional cleanup for Agora or WebRTC connections
    if (window.agoraClient) {
      try {
        await window.agoraClient.leave();
        console.log('🛑 Agora client disconnected');
      } catch (error) {
        console.log('ℹ️ Agora client already disconnected');
      }
    }

    // Clear any WebRTC peer connections
    if (window.peerConnection) {
      try {
        window.peerConnection.close();
        console.log('🛑 WebRTC peer connection closed');
      } catch (error) {
        console.log('ℹ️ WebRTC peer connection already closed');
      }
    }

  } catch (error) {
    console.error('Error in comprehensive cleanup:', error);
  }

  console.log('✅ Comprehensive camera cleanup completed');
};

/**
 * Setup global event listeners for page navigation and cleanup
 * This ensures cleanup happens on all possible exit scenarios
 */
export const setupGlobalCleanupListeners = () => {
  console.log('🎯 Setting up global camera cleanup listeners');

  // Page visibility change (tab switch, minimize)
  document.addEventListener('visibilitychange', () => {
    if (document.hidden) {
      console.log('📱 Page hidden - maintaining camera for return');
      // Don't cleanup on visibility change, just log
    }
  });

  // Before page unload (refresh, close, navigate)
  window.addEventListener('beforeunload', (event) => {
    console.log('🚪 Page unloading - emergency camera cleanup');
    emergencyCameraCleanup();
  });

  // Page hide (iOS Safari, back button)
  window.addEventListener('pagehide', (event) => {
    console.log('🚪 Page hiding - emergency camera cleanup');
    emergencyCameraCleanup();
  });

  // Page unload (final cleanup)
  window.addEventListener('unload', (event) => {
    console.log('🚪 Page unloaded - emergency camera cleanup');
    emergencyCameraCleanup();
  });

  // Popstate (back/forward navigation)
  window.addEventListener('popstate', (event) => {
    console.log('🔙 Browser navigation - emergency camera cleanup');
    emergencyCameraCleanup();
  });

  // Intercept History API for SPA navigation
  const originalPushState = window.history.pushState;
  const originalReplaceState = window.history.replaceState;

  window.history.pushState = function (...args) {
    console.log('📍 History push state - camera cleanup');
    emergencyCameraCleanup();
    return originalPushState.apply(this, args);
  };

  window.history.replaceState = function (...args) {
    console.log('📍 History replace state - camera cleanup');
    emergencyCameraCleanup();
    return originalReplaceState.apply(this, args);
  };

  // Error handlers
  window.addEventListener('error', (event) => {
    if (event.error && event.error.name === 'NotAllowedError') {
      console.log('🚫 Media permission error - cleanup camera');
      emergencyCameraCleanup();
    }
  });

  console.log('✅ Global cleanup listeners installed');
};

/**
 * Cleanup function specifically for React component unmounting
 */
export const reactComponentCleanup = () => {
  console.log('⚛️ React component cleanup - stopping camera');
  emergencyCameraCleanup();
};

/**
 * Check if camera is currently in use
 */
export const isCameraInUse = async () => {
  try {
    const videoElements = document.querySelectorAll('video');
    let inUse = false;

    videoElements.forEach(video => {
      if (video.srcObject) {
        const stream = video.srcObject;
        if (stream && typeof stream.getTracks === 'function') {
          const videoTracks = stream.getVideoTracks();
          if (videoTracks.some(track => track.readyState === 'live')) {
            inUse = true;
          }
        }
      }
    });

    return inUse;
  } catch (error) {
    console.error('Error checking camera usage:', error);
    return false;
  }
};

/**
 * Test function to verify cleanup is working
 */
export const testCameraCleanup = async () => {
  console.log('🧪 Testing camera cleanup functionality');

  try {
    // Check initial state
    const initiallyInUse = await isCameraInUse();
    console.log(`📹 Camera initially in use: ${initiallyInUse}`);

    // Run emergency cleanup
    emergencyCameraCleanup();

    // Wait and check again
    await new Promise(resolve => setTimeout(resolve, 200));
    const afterCleanup = await isCameraInUse();
    console.log(`📹 Camera in use after cleanup: ${afterCleanup}`);

    if (!afterCleanup) {
      console.log('✅ Camera cleanup test PASSED');
      return true;
    } else {
      console.log('❌ Camera cleanup test FAILED - camera still in use');
      return false;
    }

  } catch (error) {
    console.error('Error testing camera cleanup:', error);
    return false;
  }
};

// Auto-setup global listeners when this module is imported
if (typeof window !== 'undefined') {
  // Only setup once
  if (!window.__cameraCleanupListenersInstalled) {
    setupGlobalCleanupListeners();
    window.__cameraCleanupListenersInstalled = true;
  }
}

const cameraCleanup = {
  emergencyCameraCleanup,
  comprehensiveCameraCleanup,
  setupGlobalCleanupListeners,
  reactComponentCleanup,
  isCameraInUse,
  testCameraCleanup
};

export default cameraCleanup;



